import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Upload, Trash2 } from 'lucide-react';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
`;

const FormContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 40px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.large};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: background 160ms, color 160ms, border-color 160ms;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.8rem;
  margin: 0 0 30px 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Input = styled.input`
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  transition: border-color 160ms;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder { color: ${({ theme }) => theme.colors.text.muted}; }
`;

const TextArea = styled.textarea`
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  min-height: 120px;
  resize: vertical;
  transition: border-color 160ms;
  width: 100%;
  box-sizing: border-box;
  line-height: 1.65;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder { color: ${({ theme }) => theme.colors.text.muted}; }
`;

const Select = styled.select`
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  cursor: pointer;
  transition: border-color 160ms;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }

  option {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ImageUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ImageUploadBox = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 160ms, background 160ms;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
  }

  input {
    display: none;
  }
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
`;

const ImagePreview = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: ${({ theme }) => theme.colors.accent};
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: opacity 160ms;

  &:hover {
    opacity: 0.9;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 10px;
`;

const SubmitButton = styled(motion.button)`
  flex: 1;
  padding: 12px 26px;
  background: ${({ theme }) => theme.colors.accent};
  border: none;
  border-radius: 8px;
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 160ms;

  &:hover:not(:disabled) { opacity: 0.9; }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(motion.button)`
  padding: 12px 26px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 160ms, border-color 160ms;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const StudioItemForm = ({ item, onClose }) => {
    const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'mobile_app',
    status: 'prototype',
    url: '',
    githubUrl: '',
    technologies: '',
    images: []
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const db = getFirestore();
  const storage = getStorage();

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        description: item.description || '',
        type: item.type || 'mobile_app',
        status: item.status || 'prototype',
        url: item.url || '',
        githubUrl: item.githubUrl || '',
        technologies: item.technologies ? item.technologies.join(', ') : '',
        images: item.images || []
      });
    }
  }, [item]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const uploadImages = async () => {
    const uploadedUrls = [];

    for (const file of imageFiles) {
      const storageRef = ref(storage, `studio/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      uploadedUrls.push(downloadUrl);
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload new images
      const newImageUrls = await uploadImages();
      const allImages = [...formData.images, ...newImageUrls];

      const itemData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        status: formData.status,
        url: formData.url,
        githubUrl: formData.githubUrl,
        technologies: formData.technologies.split(',').map(t => t.trim()).filter(t => t),
        images: allImages,
        updatedAt: new Date().toISOString()
      };

      if (item) {
        // Update existing item
        await updateDoc(doc(db, 'studio_items', item.id), itemData);
      } else {
        // Create new item
        itemData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'studio_items'), itemData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving studio item:', error);
      alert('Failed to save item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <FormContainer
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <Title>{item ? 'Edit Product' : 'Add New Product'}</Title>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Product Name *</Label>
            <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Neural Network Visualizer"
              required
            />
          </FormGroup>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormGroup>
              <Label>Type *</Label>
              <Select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
              >
                <option value="mobile_app">Mobile App</option>
                <option value="saas">SaaS Platform</option>
                <option value="game">Game</option>
                <option value="web_app">Web App</option>
                <option value="utility">Utility</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Status *</Label>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                <option value="concept">Concept</option>
                <option value="prototype">Prototype</option>
                <option value="beta">Beta</option>
                <option value="live">Live</option>
                <option value="archived">Archived</option>
              </Select>
            </FormGroup>
          </div>

          <FormGroup>
            <Label>Description *</Label>
            <TextArea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe this product..."
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Technologies (comma separated)</Label>
            <Input
              type="text"
              name="technologies"
              value={formData.technologies}
              onChange={handleInputChange}
              placeholder="React, Three.js, WebGL"
            />
          </FormGroup>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormGroup>
              <Label>Live URL</Label>
              <Input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </FormGroup>

            <FormGroup>
              <Label>GitHub URL</Label>
              <Input
                type="url"
                name="githubUrl"
                value={formData.githubUrl}
                onChange={handleInputChange}
                placeholder="https://github.com/..."
              />
            </FormGroup>
          </div>

          <FormGroup>
            <Label>Images</Label>
            <ImageUploadSection>
              {/* Existing Images */}
              {formData.images.length > 0 && (
                <ImagePreviewGrid>
                  {formData.images.map((url, index) => (
                    <ImagePreview key={`existing-${index}`}>
                      <img src={url} alt={`Preview ${index + 1}`} />
                      <RemoveImageButton type="button" onClick={() => removeExistingImage(index)}>
                        <Trash2 size={14} />
                      </RemoveImageButton>
                    </ImagePreview>
                  ))}
                </ImagePreviewGrid>
              )}

              {/* New Images */}
              {imageFiles.length > 0 && (
                <ImagePreviewGrid>
                  {imageFiles.map((file, index) => (
                    <ImagePreview key={`new-${index}`}>
                      <img src={URL.createObjectURL(file)} alt={`New ${index + 1}`} />
                      <RemoveImageButton type="button" onClick={() => removeImage(index)}>
                        <Trash2 size={14} />
                      </RemoveImageButton>
                    </ImagePreview>
                  ))}
                </ImagePreviewGrid>
              )}

              <ImageUploadBox>
                <Upload size={40} />
                <p style={{ marginTop: '10px' }}>Click to upload images</p>
                <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>PNG, JPG, WebP up to 10MB</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                />
              </ImageUploadBox>
            </ImageUploadSection>
          </FormGroup>

          <ButtonGroup>
            <CancelButton
              type="button"
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </CancelButton>
            <SubmitButton
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? 'Saving...' : item ? 'Update Item' : 'Create Item'}
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </FormContainer>
    </Overlay>
  );
};

export default StudioItemForm;
